import React, { useCallback } from 'react';
import { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { discounts, DiscountTypes } from 'api';
import { FormInput } from 'components';
import { AutocompleteSearchItem } from 'api/types/common';
import { dataInitialSelectsOptions } from '../../../fields';

interface Props {
  control: Control<DiscountTypes.ItemCreateParams> | undefined;
  watch: UseFormWatch<DiscountTypes.ItemCreateParams>;
  setValue: UseFormSetValue<DiscountTypes.ItemCreateParams>;
  initialSelectsOptions:
    | ReturnType<typeof dataInitialSelectsOptions>
    | undefined;
}

const ProductsInput: React.FC<Props> = (props: Props): JSX.Element => {
  const { control, watch, setValue, initialSelectsOptions } = props;

  const brand = watch('brand');
  const countryTeam = watch('country_team');
  const shopId = watch('shop_id');

  const handleAutocompleteChange = useCallback(
    (
      value: AutocompleteSearchItem | AutocompleteSearchItem[] | null,
      onChange: (value: string | string[] | null) => void,
    ) => {
      if (value && Array.isArray(value)) {
        const newValue = value.map((v) => {
          if ('originData' in v) {
            return v.originData;
          }
          return {
            id: v._id.$oid,
            title: v.name,
            image: v.image,
            variant_id: v.variant_id,
          };
        });

        onChange(newValue);

        const ids = newValue.map((v) => v.variant_id);
        setValue('metafields_attributes.0.value.target_variant_ids', ids);
      }
    },
    [setValue],
  );

  const getProducts = useCallback(
    (query: string) =>
      discounts
        .autocompleteProducts({
          product_title: query,
          brand,
          shop_id: shopId,
          country_team: countryTeam,
        })
        .then((res) =>
          res.data.products.edges.map((e) => ({
            name: e.node.title,
            _id: { $oid: e.node.id },
            image: e.node.images?.edges[0]?.node.url,
            variant_id: e.node.variants?.edges[0]?.node.id,
            originData: {
              id: e.node.id,
              title: e.node.title,
              image: e.node.images?.edges[0]?.node.url,
              variant_id: e.node.variants?.edges[0]?.node.id,
            },
          })),
        ),
    [brand, countryTeam, shopId],
  );

  return (
    <FormInput
      control={control}
      name="metafields_attributes.0.value.products"
      inputType="autocompleteSearch"
      label="Targeted products discount"
      multiple
      initialValue={initialSelectsOptions?.metafieldsProducts}
      onChange={handleAutocompleteChange}
      queryKey={discounts.endpoints.autocompleteProducts}
      queryFn={getProducts}
    />
  );
};

export default ProductsInput;
